import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState, useRef, useCallback, useMemo } from 'react';
import { isEmptyObject, PluginsExtension, SuggestExtension } from '@remirror/core';
import { hasStateChanged } from '@remirror/extension-positioner';
import { useRemirrorContext, useExtension } from '@remirror/react-core';

/**
 * This hook allows you to dynamically create a suggester which can respond to
 * user input of activation characters or regex patterns.
 *
 * By adding a suggester it is possible to keep track of what the user has
 * typed and receive meaningful information in return.
 *
 * This includes
 *
 * - The range of the matching text
 * - The matching text value
 * - The query value, which excludes the matching character (or character regex).
 * - The matching capture groups [FULL_MATCH, MATCHING_CHARACTER, CUSTOM_GROUPS]
 *
 * The return value has two keys, `exit` and `change` which can both be
 * `undefined`. The reason for including both the `exit` and `change` return
 * values is that it's possible for both to occur at the the same time during a
 * **jump** from one [[`Suggester`]] _match_ to another suggester match.
 *
 * The cursor has exited and entered (changed) at the same time.
 */

function useSuggest(props) {
  var _useRemirrorContext = useRemirrorContext(),
      helpers = _useRemirrorContext.helpers;

  var _useState = useState(() => _objectSpread({
    change: undefined,
    exit: undefined,
    shouldResetChangeState: false,
    shouldResetExitState: false
  }, helpers.getSuggestMethods())),
      _useState2 = _slicedToArray(_useState, 2),
      hookState = _useState2[0],
      setHookState = _useState2[1]; // Keep track of changes from state updates.


  var stateRef = useRef({}); // Track changes in the suggester

  var onChange = useCallback(options => {
    var changeReason = options.changeReason,
        exitReason = options.exitReason,
        match = options.match,
        query = options.query,
        text = options.text,
        range = options.range; // Set the state update to be the changes that have happened since the last
    // change event.

    var stateUpdate = _objectSpread({}, stateRef.current); // Reset the state ref.


    stateRef.current = {}; // Keep track of changes

    if (changeReason) {
      stateUpdate.change = {
        match,
        query,
        text,
        range,
        reason: changeReason
      };
      stateUpdate.shouldResetChangeState = false;

      if (!exitReason) {
        stateUpdate.exit = undefined;
      }
    }

    if (exitReason) {
      stateUpdate.exit = {
        match,
        query,
        text,
        range,
        reason: exitReason
      };
      stateUpdate.shouldResetExitState = false;

      if (!changeReason) {
        stateUpdate.change = undefined;
      }
    }

    if (!isEmptyObject(stateUpdate)) {
      setHookState(prevState => _objectSpread(_objectSpread({}, prevState), stateUpdate));
    }
  }, []); // This change handler will be called on every editor state update. It keeps
  // the state for the suggester that has been added correct.

  var onApplyState = useCallback(_ref => {
    var tr = _ref.tr,
        state = _ref.state,
        previousState = _ref.previousState;

    if (!hasStateChanged({
      tr,
      state,
      previousState
    }) || helpers.getSuggestState().removed) {
      return;
    } // Copy the values from the current stateRef.


    var stateUpdate = _objectSpread({}, stateRef.current);

    if ((hookState.shouldResetChangeState || stateUpdate.shouldResetChangeState) && hookState.change) {
      stateUpdate.change = undefined;
    }

    if ((hookState.shouldResetExitState || stateUpdate.shouldResetExitState) && hookState.exit) {
      stateUpdate.exit = undefined;
    }

    if (!(hookState.shouldResetChangeState || stateUpdate.shouldResetChangeState) && hookState.change) {
      stateUpdate.shouldResetChangeState = true;
    }

    if (!(hookState.shouldResetExitState || stateUpdate.shouldResetExitState) && hookState.exit) {
      stateUpdate.shouldResetExitState = true;
    }

    stateRef.current = stateUpdate;
  }, [hookState, helpers]); // Attach the editor state handler to the instance of the remirror editor.

  useExtension(PluginsExtension, p => p.addHandler('applyState', onApplyState), [onApplyState]); // Add the suggester to the editor via the `useExtension` hook.

  useExtension(SuggestExtension, p => p.addCustomHandler('suggester', _objectSpread(_objectSpread({}, props), {}, {
    onChange
  })), [onChange, ...Object.values(props)]);
  return useMemo(() => ({
    addIgnored: hookState.addIgnored,
    change: hookState.change,
    exit: hookState.exit,
    clearIgnored: hookState.clearIgnored,
    ignoreNextExit: hookState.ignoreNextExit,
    removeIgnored: hookState.removeIgnored,
    setMarkRemoved: hookState.setMarkRemoved
  }), [hookState.addIgnored, hookState.change, hookState.clearIgnored, hookState.exit, hookState.ignoreNextExit, hookState.removeIgnored, hookState.setMarkRemoved]);
}
/**
 * @deprecated - use `useSuggest`
 */

var useSuggester = useSuggest;

export { useSuggest, useSuggester };
